import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

import { useApproveBundle, useUpdateBundle } from '~/hooks/apis/bundle/useBundles';

import { Bundle } from '~/types/bundles/bundle';

import TourOfferName from './TourOfferName';
import { BuilderStep, CheckState, ReadinessCheck } from './useBundleReadiness';

interface Props {
  bundle: Bundle;
  checks: Array<ReadinessCheck>;
  blockers: Array<ReadinessCheck>;
  onGoToStep: (step: BuilderStep) => void;
}

const CHECK_ICONS: Record<CheckState, React.ReactNode> = {
  done: <CheckCircleIcon color="success" />,
  todo: <RadioButtonUncheckedIcon color="disabled" />,
  warning: <WarningAmberIcon color="warning" />,
  checking: <CircularProgress size={20} />,
};

/** Approving pairs the tour with the hotel and creates the bundle's LTLE, ready for its content. */
export default function BundleApprovalStep({ bundle, checks, blockers, onGoToStep }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const approveBundle = useApproveBundle(bundle.id);
  const updateBundle = useUpdateBundle(bundle.id);

  const tour = bundle.components.find((component) => component.product_type === 'tour');
  const warnings = checks.filter((check) => check.state === 'warning');

  const handleApprove = async () => {
    try {
      await approveBundle.mutateAsync(bundle);
      setIsConfirmOpen(false);
      enqueueSnackbar('Bundle approved and its LTLE created', { variant: 'success' });
      onGoToStep('content');
    } catch {
      // The mutation error is shown in the dialog.
    }
  };

  const closeConfirm = () => {
    setIsConfirmOpen(false);
    approveBundle.reset();
  };

  const handleMoveToDraft = async () => {
    try {
      await updateBundle.mutateAsync({ status: 'draft' });
      enqueueSnackbar('Bundle moved back to draft', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  if (bundle.status === 'approved') {
    return (
      <Stack spacing={3}>
        <Card variant="outlined" sx={{ p: 3, borderColor: 'success.main' }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <VerifiedIcon color="success" fontSize="large" />
            <Box flex={1}>
              <Typography variant="h6">Approved</Typography>
              <Typography color="text.secondary">
                The bundle's LTLE, {tour ? <TourOfferName tourOfferId={tour.tour_offer_id} /> : 'its tour'}, was made
                from the base tour with a copy of its content. The tour, hotel and departures are now fixed.
              </Typography>
              <Stack direction="row" spacing={1} mt={2}>
                <Button variant="contained" onClick={() => onGoToStep('content')}>
                  Edit content
                </Button>
                <Button onClick={() => onGoToStep('prices')}>Set prices</Button>
              </Stack>
            </Box>
          </Stack>
        </Card>

        <Box>
          <Typography variant="subtitle2">Need to change the tour, hotel or departures?</Typography>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Moving back to draft unlocks them. The LTLE and its content stay as they are.
          </Typography>
          <Button onClick={handleMoveToDraft} disabled={updateBundle.isPending}>
            Move back to draft
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Card variant="outlined">
        <List disablePadding>
          {checks.map((check, index) => (
            <React.Fragment key={check.key}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                secondaryAction={
                  check.state !== 'done' &&
                  check.state !== 'checking' && (
                    <Button size="small" onClick={() => onGoToStep(check.step)}>
                      {check.state === 'warning' ? 'Review' : 'Fix'}
                    </Button>
                  )
                }
              >
                <ListItemIcon>{CHECK_ICONS[check.state]}</ListItemIcon>
                <ListItemText
                  primary={check.label}
                  secondary={check.detail}
                  secondaryTypographyProps={{ color: check.state === 'warning' ? 'warning.main' : 'text.secondary' }}
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Card>

      <Alert severity="info">
        Approving creates the bundle's limited time LE tour (LTLE) from the base tour, with its own copy of the tour's
        content to edit. The tour, hotel and departures are then fixed; prices can still change. A tour manager's
        sign-off on the pairing will be added here.
      </Alert>

      {warnings.length > 0 && blockers.length === 0 && (
        <Alert severity="warning">
          You can approve with departures that can't be sold, but they stay flagged until they're removed or available.
        </Alert>
      )}

      <Box>
        <Button variant="contained" size="large" onClick={() => setIsConfirmOpen(true)} disabled={blockers.length > 0}>
          Approve bundle
        </Button>
        {blockers.length > 0 && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            {blockers.length === 1 ? '1 thing' : `${blockers.length} things`} left before it can be approved.
          </Typography>
        )}
      </Box>

      <Dialog open={isConfirmOpen} onClose={approveBundle.isPending ? undefined : closeConfirm}>
        <DialogTitle>Approve {bundle.name}?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              This creates the bundle's LTLE and fixes its tour, hotel and departures.
            </DialogContentText>
            {!!approveBundle.error && (
              <Alert severity="error">
                {approveBundle.error.message} Nothing is lost: approving again carries on from where it stopped.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeConfirm} disabled={approveBundle.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApprove} disabled={approveBundle.isPending}>
            {approveBundle.isPending ? 'Approving…' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
